import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { filter, fromEvent, Subject, take, takeUntil } from 'rxjs';
import { AppConfig } from './config/service.config';
import { AuthService } from './core/auth/auth.service';
import { ExportJobsWidgetComponent } from './core/export-jobs/export-jobs-widget.component';
import { UserService } from './core/user/user.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [RouterOutlet, ExportJobsWidgetComponent],
})
export class AppComponent implements OnInit, OnDestroy {
    private _authService = inject(AuthService);
    private _userService = inject(UserService);
    private _router = inject(Router);
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor() {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Initial check on startup
        this.validateSecurityStatus();

        // Re-validate user status when app returns to foreground (tab switch/visibility change)
        fromEvent(document, 'visibilitychange')
            .pipe(
                filter(() => document.visibilityState === 'visible'),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                this.validateSecurityStatus();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Validate security status
     */
    private validateSecurityStatus(): void {
        const loggedIn = this._authService.isLoggedIn();
        const userId = localStorage.getItem('userId');

        console.log('[Security] validation trigger - Is Logged In:', loggedIn);

        if (!loggedIn || !userId) {
            return;
        }

        // 🔥 authoritative check: Call Suspension API directly
        this._userService
            .checkSuspensionStatus(userId)
            .pipe(take(1))
            .subscribe({
                next: (status) => {
                    console.log('[Security] Suspension API response:', status);
                    if (status?.suspended) {
                        console.warn(
                            '[Security] 🚨 User suspended via API! Forcing logout.'
                        );
                        this.forceLogoutUser();
                    } else {
                        console.log('[Security] ✅ Suspension status: CLEAR.');
                    }
                },
                error: (err) =>
                    console.warn('[Security] Suspension API failed:', err),
            });

        // 🔥 2. Refresh user profile
        this._userService
            .getUser()
            .pipe(take(1))
            .subscribe({
                next: (user) => {
                    console.log(
                        '[Security] User profile refreshed. ID No:',
                        user?.identification?.idNo
                    );
                },
                error: (err) =>
                    console.warn('[Security] Profile refresh failed:', err),
            });
    }

    /**
     * Force logout user
     */
    private forceLogoutUser(): void {
        this._authService.signOut().subscribe(() => {
            this._router.navigate(['/sign-in'], {
                replaceUrl: true,
            });
        });
    }
}

export function initializeApp(appConfig: AppConfig) {
    return () => appConfig.load();
}
